import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Separator } from './ui/separator';
import { 
  Scale, 
  Users, 
  FileCheck, 
  Building, 
  Heart,
  ShieldCheck,
  Facebook,
  Instagram,
  Mail,
  CheckCircle,
  Sparkles,
  Lightbulb,
  ChevronLeft,
  ChevronRight,
  MapPin
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const { toast } = useToast();
  const [petitionData, setPetitionData] = useState({
    name: '',
    email: '',
    zipCode: ''
  });
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [currentSlide, setCurrentSlide] = useState(0);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);

  // Helper to get full image URL (for uploaded images)
  const getImageUrl = (url) => {
    if (url && url.startsWith('/api/uploads/')) {
      return `${API_URL}${url}`;
    }
    return url;
  };

  // Fetch gallery images from backend
  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        const response = await fetch(`${API_URL}/api/gallery`);
        if (response.ok) {
          const data = await response.json();
          // Transform URLs for uploaded images
          const transformedData = data.map(img => ({
            ...img,
            url: getImageUrl(img.url)
          }));
          setGalleryImages(transformedData);
        }
      } catch (error) {
        console.error('Error fetching gallery images:', error);
      } finally {
        setGalleryLoading(false);
      }
    };
    
    fetchGalleryImages();
  }, []);

  // Fallback images in case backend is not available
  const fallbackImages = [
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/x9cc76yr_Screenshot%202025-11-08%20at%204.12.43%E2%80%AFPM.png',
      alt: 'Judge Hernandez Sama with family and community'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/dhi76rr0_IMG_2375.jpg',
      alt: 'Judge Hernandez Sama at community event'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/dy24fk8g_IMG_2377.jpg',
      alt: 'Judge Hernandez Sama serving the community'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/aanecqqe_IMG_2374.jpg',
      alt: 'Judge Hernandez Sama in action'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/u07g3my1_IMG_0957.JPEG',
      alt: 'Judge Jen with community members'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/ucsm4l6w_PJP-0065.JPG',
      alt: 'Judge Jen with Arizona judges'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/jjkb1igo_IMG_7058.JPEG',
      alt: 'Judge Jen at her courtroom'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/8pdo7py0_image000003.JPG',
      alt: 'Judge Jen serving justice in the courtroom'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/7i38agav_1A4D4156-04C7-47DF-96C5-21141C0F4963.JPG',
      alt: 'Judge Jen community advocate'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/3r9ilz66_PJP-105.JPG',
      alt: 'Judge Jen with colleagues'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/1fjnkwwv_96B9B7F5-FFD9-4790-BB5F-A12C656439D3.JPG',
      alt: 'Judge Jen at community engagement'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/idfhcw35_image000000.JPEG',
      alt: 'Judge Jen in judicial service'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/w311ie36_IMG_0460.JPG',
      alt: 'Judge Jen with community leaders'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/jg5gkg0q_IMG_3685.JPEG',
      alt: 'Judge Jen serving El Centro Justice Court'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/azhre7yn_IMG_0658.JPG',
      alt: 'Judge Jen on Arizona Midday'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/a373dple_IMG_0542.JPG',
      alt: 'Judge Jen with her father'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/ybpngufh_Screenshot%202025-12-04%20at%205.30.54%E2%80%AFPM.png',
      alt: 'Judge Jen with community delegation'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/cyteddeh_Screenshot%202025-12-04%20at%205.29.19%E2%80%AFPM.png',
      alt: 'Judge Jen with elderly community member'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/ayicrd0f_Screenshot%202025-12-04%20at%205.27.14%E2%80%AFPM.png',
      alt: 'Judge Jen at her desk with mentor'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/epzntz21_Screenshot%202025-12-04%20at%205.31.13%E2%80%AFPM.png',
      alt: 'Judge Jen with civic leaders'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/zcd9tknt_Screenshot%202025-12-04%20at%205.31.23%E2%80%AFPM.png',
      alt: 'Judge Jen at community gathering'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/h8grjn1g_Screenshot%202025-12-04%20at%205.31.46%E2%80%AFPM.png',
      alt: 'Judge Jen with community supporters'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/5z6zyz5f_Screenshot%202025-12-04%20at%205.31.59%E2%80%AFPM.png',
      alt: 'Judge Jen community outreach'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/lenuw8gx_Screenshot%202025-12-04%20at%205.33.23%E2%80%AFPM.png',
      alt: 'Judge Jen with Arizona community members'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/6a026nvq_Screenshot%202025-12-04%20at%205.33.46%E2%80%AFPM.png',
      alt: 'Young Judge Jen'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/0chh3feg_Screenshot%202025-12-04%20at%205.34.09%E2%80%AFPM.png',
      alt: 'Judge Jen with her grandmother'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/7ejw3gh4_Screenshot%202025-12-04%20at%205.34.41%E2%80%AFPM.png',
      alt: 'Judge Jen with her parents'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/ngapcyiy_Screenshot%202025-12-04%20at%205.34.55%E2%80%AFPM.png',
      alt: 'Judge Jen celebrating Dia de los Muertos'
    }
  ];

  // Use fetched images or fallback if empty
  const displayImages = galleryImages.length > 0 ? galleryImages : fallbackImages;

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => new Set([...prev, entry.target.id]));
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // Auto-advance slideshow
  useEffect(() => {
    if (displayImages.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % displayImages.length);
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [displayImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % displayImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + displayImages.length) % displayImages.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const handlePetitionSubmit = (e) => {
    e.preventDefault();
    console.log('Petition submitted:', petitionData);
    toast({
      title: "Thank you for your support!",
      description: "You've been added to our campaign supporters.",
    });
    setPetitionData({ name: '', email: '', zipCode: '' });
  };

  const accomplishments = [
    {
      icon: Scale,
      title: 'Enhanced Access to Justice',
      description: 'Virtual hearings, electronic filing, and equitable access to legal resources. Modernized court operations for accessibility and ease.',
      color: '#3b82f6'
    },
    {
      icon: ShieldCheck,
      title: 'Fairness for All Parties',
      description: 'Streamlined procedures for timely, respectful consideration. Support for both plaintiffs and defendants.',
      color: '#8b5cf6'
    },
    {
      icon: Users,
      title: 'Strengthened Community Engagement',
      description: 'Collaborates with local organizations and community leaders. Connects residents to housing, employment, and treatment resources.',
      color: '#ec4899'
    },
    {
      icon: FileCheck,
      title: 'Transparency & Efficiency',
      description: 'Restored public trust through clear, transparent processes. Ensures everyone understands their rights and what to expect.',
      color: '#f59e0b'
    },
    {
      icon: Heart,
      title: 'Community-Competent Justice',
      description: 'Prioritizes empathy, accountability, and safety. Promotes growth, opportunity, and neighborhood trust.',
      color: '#10b981'
    },
    {
      icon: Lightbulb,
      title: 'Innovative Court Leadership',
      description: 'Championed technology-driven solutions and data-informed decisions to reduce backlogs, improve case management, and make the justice system more responsive to community needs.',
      color: '#06b6d4'
    }
  ];

  return (
    <div className="landing-page">
      {/* Decorative Background Elements */}
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
      </div>

      {/* Header Navigation */}
      <header className="header">
        <div className="header-content">
          <div className="logo animate-slide-down">Judge Jen For Justice</div>
          <nav className="nav-links">
            <a href="#about" className="nav-link">About Us</a>
            <a href="#gallery" className="nav-link">Gallery</a>
            <a href="#endorsements" className="nav-link">Endorsements</a>
            <a href="#contact" className="nav-link">Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-badge animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <Sparkles size={16} className="badge-icon" />
              Judge Jen For Justice
            </div>
            <h1 className="hero-title">
              <span className="animate-slide-up" style={{ animationDelay: '0.4s' }}>Re-Elect Judge Jen Hernandez Sama</span>
            </h1>
            <p className="hero-subtitle animate-slide-up" style={{ animationDelay: '0.8s' }}>
              for El Centro Justice of the Peace, DTPHX.
            </p>
            <p className="hero-tagline animate-fade-in" style={{ animationDelay: '1s' }}>
              The Progressive Voice in Our Court.
            </p>
            <div className="hero-cta animate-fade-in" style={{ animationDelay: '1.2s' }}>
              <Button className="cta-primary" onClick={() => window.open('https://go.azsos.gov/5drz', '_blank')}>
                <span>Sign the Petition</span>
                <div className="button-shine"></div>
              </Button>
              <Button className="cta-donate" onClick={() => window.open('https://secure.actblue.com/donate/judge-jen-hernandez-sama-2', '_blank')}>
                <span>Donate</span>
                <div className="button-shine"></div>
              </Button>
              <Button variant="outline" className="cta-secondary" onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}>
                Learn More
              </Button>
            </div>
          </div>
          <div className="hero-image animate-float">
            <div className="image-glow"></div>
            <img 
              src="https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/36lh2k7e_u9867255821_nothing_--ar_5584_--v_7_44efa8db-782a-4a98-ad42-593c8eb514c3_3.png" 
              alt="Judge Jennifer Hernandez Sama"
              className="animate-scale-in"
              style={{ animationDelay: '0.5s' }}
            />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section" data-animate>
        <div className="section-container">
          <h2 className={`section-title ${visibleSections.has('about') ? 'animate-slide-up' : 'opacity-0'}`}>
            Meet Judge Jen Hernandez Sama
          </h2>
          <div className="about-text-left">
            <p className={`${visibleSections.has('about') ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
              Justice of the Peace, Jennifer Hernandez Sama is seeking Re-Election in 2026 and is currently serving the Downtown Phoenix El Centro Court since 2023. Since taking office Judge Jen has brought integrity, restored Compliance, and improved efficiency to her court.
            </p>
            <p className={`${visibleSections.has('about') ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.4s' }}>
              Judge Jen is a proud 8th generation Arizonan, attended Phoenix Union HSD and is a graduate of Arizona State University. Her roots are here, her commitment is here and she serves her community with integrity, fairness and compassion everyday. She is committed to ensuring that every person regardless of background or circumstance has a fair opportunity to be heard and treated with respect.
            </p>
            <p className={`${visibleSections.has('about') ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.6s' }}>
              As a progressive voice in the judiciary, Judge Jen has modernized court operations to make them more transparent and user-friendly. She leads with accountability and understanding, creating a courtroom environment that balances compassion with responsibility. Her leadership has strengthened public trust, improved efficiency, and set a higher standard for equitable justice in Maricopa County.
            </p>
            <p className={`${visibleSections.has('about') ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.8s' }}>
              Beyond the courtroom, Judge Jen is deeply involved in her community. Appointed by Chief Justice Ann Timmer, she is the only JP in Maricopa County to serve on the "Court Interpreter Program Advisory Committee" (CIPAC). She also serves on the Maricopa County Regional Homeless Court Governing Council, volunteers with initiatives such as Veterans StandDown, and mentors young people pursuing public service and law. Her life's work reflects her commitment to building a justice system that mirrors the community it serves modern, inclusive, and fair for all.
            </p>
          </div>
          <div className="about-content">
            <div id="gallery" className={`about-gallery ${visibleSections.has('about') ? 'animate-scale-in' : 'opacity-0'}`} style={{ animationDelay: '1.0s' }}>
              <div className="gallery-container">
                <div className="gallery-slides">
                  {displayImages.map((image, index) => (
                    <div
                      key={image.id || index}
                      className={`gallery-slide ${index === currentSlide ? 'active' : ''}`}
                    >
                      <img src={image.url} alt={image.alt} />
                    </div>
                  ))}
                </div>

                {/* Navigation Arrows - visible on hover */}
                <button 
                  className="gallery-nav gallery-nav-prev" 
                  onClick={prevSlide}
                  aria-label="Previous slide"
                >
                  <ChevronLeft size={24} />
                </button>
                <button 
                  className="gallery-nav gallery-nav-next" 
                  onClick={nextSlide}
                  aria-label="Next slide"
                >
                  <ChevronRight size={24} />
                </button>
              </div>
            </div>
            <div className="about-stats">
              {['8th', '2023', '100%'].map((stat, index) => (
                <div 
                  key={index}
                  className={`stat-card ${visibleSections.has('about') ? 'animate-pop-in' : 'opacity-0'}`}
                  style={{ animationDelay: `${1.0 + index * 0.2}s` }}
                >
                  <div className="stat-number">{stat}</div>
                  <div className="stat-label">
                    {index === 0 ? 'Generation Arizonan' : index === 1 ? 'Serving Since' : 'Committed to Justice'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Record */}
      <section id="leadership" className="leadership-section" data-animate>
        <div className="section-container">
          <h2 className={`section-title ${visibleSections.has('leadership') ? 'animate-slide-up' : 'opacity-0'}`}>
            As Justice of the Peace, Judge Jen Has:
          </h2>
          <div className="accomplishments-grid">
            {accomplishments.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Card 
                  key={index} 
                  className={`accomplishment-card ${visibleSections.has('leadership') ? 'animate-slide-up' : 'opacity-0'}`}
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <CardContent className="accomplishment-content">
                    <div className="accomplishment-icon" style={{ background: `linear-gradient(135deg, ${item.color} 0%, ${item.color}dd 100%)` }}>
                      <IconComponent size={32} />
                    </div>
                    <h3 className="accomplishment-title">{item.title}</h3>
                    <p className="accomplishment-description">{item.description}</p>
                    <div className="card-shine"></div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Legacy Section */}
      <section className="legacy-section" data-animate>
        <div className="legacy-container">
          <div className="legacy-overlay"></div>
          <div className="legacy-content">
            <h2 className={`legacy-title ${visibleSections.has('leadership') ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
              Following in the footsteps of her father<br />Former Judge Jimmie R. Hernandez
            </h2>
          </div>
        </div>
      </section>

      {/* Endorsements Section */}
      <section id="endorsements" className="endorsements-section" data-animate>
        <div className="section-container">
          <h2 className={`section-title ${visibleSections.has('endorsements') ? 'animate-slide-up' : 'opacity-0'}`}>
            Community Endorsements
          </h2>
          <p className={`section-subtitle ${visibleSections.has('endorsements') ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
            Proud to have the support of dedicated community leaders and advocates
          </p>
          <div className="endorsements-grid">
            <Card className={`endorsement-card ${visibleSections.has('endorsements') ? 'animate-slide-up' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
              <CardContent className="endorsement-content">
                <div className="endorsement-image">
                  <img src="https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/96tcsyg2_Screenshot%202026-02-23%20at%205.08.47%E2%80%AFPM.png" alt="Dr. Louie Olives" />
                </div>
                <h3 className="endorsement-name">Dr. Louie Olives</h3>
                <p className="endorsement-title">Professor Emeritus<br />ASU</p>
                <p className="endorsement-quote">
                  "I proudly support the re-election of Judge Jen Hernandez Sama for Justice of the Peace at the El Centro Justice Court. A Phoenix native, and a graduate of Arizona State University, Judge Hernandez Sama brings proven leadership, experience, and a strong commitment to community service. I strongly endorse her re-election."
                </p>
              </CardContent>
            </Card>

            <Card className={`endorsement-card ${visibleSections.has('endorsements') ? 'animate-slide-up' : 'opacity-0'}`} style={{ animationDelay: '0.5s' }}>
              <CardContent className="endorsement-content">
                <div className="endorsement-dual">
                  <div className="endorsement-person">
                    <div className="endorsement-image">
                      <img src="https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/rn2rygpf_Screenshot%202026-02-23%20at%205.08.56%E2%80%AFPM.png" alt="Mary Rose Wilcox" />
                    </div>
                    <h3 className="endorsement-name">Mary Rose Wilcox</h3>
                    <p className="endorsement-title">Former Phoenix City Council Member & Maricopa County Supervisor</p>
                  </div>
                  <div className="endorsement-person">
                    <div className="endorsement-image">
                      <img src="https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/dlg846u8_IMG_3312.JPEG" alt="Earl Wilcox" />
                    </div>
                    <h3 className="endorsement-name">Earl Wilcox</h3>
                    <p className="endorsement-title">Former Member, Arizona State Legislature</p>
                  </div>
                </div>
                <p className="endorsement-quote">
                  "Judge Jen Hernandez Sama is a proven leader and dedicated advocate for the residents of the El Centro Justice Court. As Justice of the Peace, she has strengthened court operations, expanded community education, and increased neighborhood engagement through public forums and outreach. We urge your support to re-elect Judge Hernandez Sama so she may continue serving our community with fairness, equity, and dedication to public service."
                </p>
              </CardContent>
            </Card>

            <Card className={`endorsement-card ${visibleSections.has('endorsements') ? 'animate-slide-up' : 'opacity-0'}`} style={{ animationDelay: '0.7s' }}>
              <CardContent className="endorsement-content">
                <div className="endorsement-image">
                  <img src="https://customer-assets.emergentagent.com/job_judge-jen-sama/artifacts/rkhx5zmi_Screenshot%202026-02-23%20at%205.08.52%E2%80%AFPM.png" alt="Sarah Liguori" />
                </div>
                <h3 className="endorsement-name">Sarah Liguori</h3>
                <p className="endorsement-title">LD5 State Representative</p>
                <p className="endorsement-quote">
                  "I am extremely proud to support and endorse the re-election of Judge Jen Hernandez Sama"
                </p>
              </CardContent>
            </Card>

            <Card className={`endorsement-card ${visibleSections.has('endorsements') ? 'animate-slide-up' : 'opacity-0'}`} style={{ animationDelay: '0.9s' }}>
              <CardContent className="endorsement-content">
                <div className="endorsement-image">
                  <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_25d0441d-66c0-483c-a6a6-9b1c98a5197c/artifacts/9roa8pnm_IMG_5734%202.JPEG" alt="Irma Payan" />
                </div>
                <h3 className="endorsement-name">Irma Payan</h3>
                <p className="endorsement-title">Educator, Storyteller & Community Voice</p>
                <p className="endorsement-quote">
                  "It is with great pleasure that I endorse Jennifer Hernandez Sama for re-election as El Centro Justice of the Peace. Judge Jennifer has demonstrated a deep passion for her community and has consistently proven to be fair and just in her rulings. She has dedicated herself to ensuring that our community's needs are met with integrity and commitment."
                </p>
              </CardContent>
            </Card>

            <Card className={`endorsement-card ${visibleSections.has('endorsements') ? 'animate-slide-up' : 'opacity-0'}`} style={{ animationDelay: '1.1s' }}>
              <CardContent className="endorsement-content">
                <div className="endorsement-image">
                  <img src="https://cdn.phototourl.com/free/2026-07-20-7261505b-aded-475d-acf2-440767d11116.jpg" alt="Senator Catherine Miranda" />
                </div>
                <h3 className="endorsement-name">Senator Catherine Miranda</h3>
                <p className="endorsement-title">Arizona State Senate LD11</p>
                <p className="endorsement-quote">
                  "Judge Jen Hernandez Sama has been a dedicated, fair-minded leader for our community. I proudly endorse her re-election as El Centro Justice of the Peace with integrity and commitment."
                </p>
              </CardContent>
            </Card>

            <Card className={`endorsement-card ${visibleSections.has('endorsements') ? 'animate-slide-up' : 'opacity-0'}`} style={{ animationDelay: '1.3s' }}>
              <CardContent className="endorsement-content">
                <div className="endorsement-image">
                  <img src="https://cdn.phototourl.com/free/2026-07-20-c63ce3a0-ae7e-41d3-b751-e64d8b3ebd0b.jpg" alt="Dana L. Burns M. ED" />
                </div>
                <h3 className="endorsement-name">Dana L. Burns M. ED</h3>
                <p className="endorsement-title">Executive Director</p>
                <p className="endorsement-quote">
                  "It is with great pleasure that I endorse Jennifer Hernandez Sama for re-election as El Centro Justice of the Peace. Judge Jennifer has shown unwavering leadership, integrity, and a true commitment to our community. She listens, she cares, and she works tirelessly to ensure that justice is fair, accessible, and rooted in respect for every individual."
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values & Vision */}
      <section className="values-section" data-animate>
        <div className="section-container">
          <p className={`values-text ${visibleSections.has('leadership') ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
            Since taking office, Judge Jen has restored trust, improved court efficiency, 
            and led with values rooted in our diverse community.
          </p>
          <div className="values-highlights">
            {['Accountability', 'Public Safety', 'Equity'].map((value, index) => (
              <div 
                key={index}
                className={`value-item ${visibleSections.has('leadership') ? 'animate-pop-in' : 'opacity-0'}`}
                style={{ animationDelay: `${0.4 + index * 0.15}s` }}
              >
                <CheckCircle className="value-icon" />
                <span>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action / Petition Form */}
      <section id="support" className="cta-section" data-animate>
        <div className="section-container">
          <div className="cta-content">
            <div className="cta-text">
              <h2 className={`cta-title ${visibleSections.has('support') ? 'animate-slide-up' : 'opacity-0'}`}>
                Re-Elect a Proven Leader
              </h2>
              <p className={`cta-description ${visibleSections.has('support') ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
                Join our movement to continue building a justice system that serves everyone fairly and compassionately.
              </p>
            </div>
            <Card className={`petition-card ${visibleSections.has('support') ? 'animate-slide-up' : 'opacity-0'}`} style={{ animationDelay: '0.4s' }}>
              <CardContent className="petition-content">
                <h3 className="petition-title">Sign the Petition</h3>
                <p className="petition-description">
                  Join our movement by signing the official petition to support Judge Jennifer Hernandez Sama's re-election campaign.
                </p>
                <Button 
                  className="petition-submit" 
                  onClick={() => window.open('https://go.azsos.gov/5drz', '_blank')}
                >
                  <span>Sign Petition</span>
                  <div className="button-shine"></div>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-contact-wrapper">
              <h4 className="footer-title">Connect With Us</h4>
              <div className="footer-column">
                <a href="https://www.facebook.com/media/set/?set=a.133561455999102&type=3" target="_blank" rel="noopener noreferrer" className="social-link">
                  <Facebook size={24} />
                  <span>Facebook</span>
                </a>
                <a href="mailto:support@judgejenforjustice.com" className="social-link">
                  <Mail size={24} />
                  <span>support@judgejenforjustice.com</span>
                </a>
                <div className="social-link">
                  <MapPin size={24} />
                  <span>El Centro Justice Court, DTPHX</span>
                </div>
                <div className="social-link">
                  <Mail size={24} />
                  <span>Phone: 602-525-8825</span>
                </div>
                <p className="footer-tagline-left">Committed to Accountable, Compassionate, and Equitable Justice.</p>
              </div>
            </div>
          </div>
          <Separator className="footer-separator" />
          <div className="footer-bottom">
            <p className="footer-disclaimer">Paid for by Committee to Re-Elect Judge Jen for Justice.</p>
            <p className="footer-copyright">© 2025 Judge Jennifer Hernandez Sama Campaign. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;